/**
 * Bundle Builder
 * --------------
 * Custom element that powers the "bundle-builder" section.
 *
 * Features:
 *  - Toggle products in/out of the bundle by clicking the card image.
 *  - Per-product variant selector (dropdown) with live price updates.
 *  - Tiered progress bar ("Buy 3 = 10% off, Buy 4 = 15% off, ...").
 *  - Live totals: original price, discounted price and savings.
 *  - Optional per-tier discount codes applied through /discount/{code}.
 *  - Adds all selected variants through /cart/add.js and opens the
 *    theme cart drawer (or redirects to the cart page).
 *
 * No external dependencies. Integrates with the theme through:
 *  - theme.routes.cartAdd / theme.routes.cartPage
 *  - theme.settings.cartType ('drawer' | 'page')
 *  - theme.Currency.formatMoney (falls back to an internal formatter)
 *  - document event 'ajaxProduct:added' (theme.js rebuilds + opens drawer)
 */

(function () {
  'use strict';

  if (customElements.get('bundle-builder')) {
    return;
  }

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.selected = new Map(); // productId -> { variantId, price }
      this.isAdding = false;
    }

    connectedCallback() {
      this.config = this.parseConfig();
      if (!this.config) return;

      this.cards = Array.from(this.querySelectorAll('[data-bb-card]'));
      this.messageEl = this.querySelector('[data-bb-message]');
      this.segments = Array.from(this.querySelectorAll('[data-bb-segment]'));
      this.badges = Array.from(this.querySelectorAll('[data-bb-badge]'));
      this.totalOriginalEl = this.querySelector('[data-bb-total-original]');
      this.totalFinalEl = this.querySelector('[data-bb-total-final]');
      this.savingsEl = this.querySelector('[data-bb-savings]');
      this.addButton = this.querySelector('[data-bb-add]');
      this.addButtonLabel = this.addButton ? this.addButton.querySelector('[data-bb-add-label]') : null;

      this.initCards();
      this.initAddButton();
      this.update();
    }

    parseConfig() {
      var configEl = this.querySelector('[data-bb-config]');
      if (!configEl) return null;
      try {
        var config = JSON.parse(configEl.textContent);
        // Always evaluate tiers from lowest to highest quantity
        config.tiers.sort(function (a, b) {
          return a.qty - b.qty;
        });
        return config;
      } catch (error) {
        console.error('BundleBuilder: invalid config JSON', error);
        return null;
      }
    }

    initCards() {
      this.cards.forEach(
        function (card) {
          var productId = card.getAttribute('data-product-id');
          var variants = [];
          var variantsEl = card.querySelector('[data-bb-variants]');
          try {
            variants = JSON.parse(variantsEl.textContent);
          } catch (error) {
            variants = [];
          }

          var isAvailable = card.getAttribute('data-available') === 'true';
          if (!isAvailable || !variants.length) {
            card.classList.add('bb-card--disabled');
            return;
          }

          // Default to the first available variant
          var currentVariant =
            variants.find(function (v) {
              return v.available;
            }) || variants[0];
          card._bbVariant = currentVariant;

          // Toggle selection when clicking the image wrapper
          var toggle = card.querySelector('[data-bb-toggle]');
          if (toggle) {
            toggle.addEventListener(
              'click',
              function () {
                this.toggleCard(card, productId);
              }.bind(this)
            );
          }

          // Variant dropdown
          var select = card.querySelector('[data-bb-variant-select]');
          if (select) {
            select.addEventListener(
              'change',
              function () {
                var variantId = parseInt(select.value, 10);
                var variant = variants.find(function (v) {
                  return v.id === variantId;
                });
                if (!variant) return;
                card._bbVariant = variant;
                this.renderCardPrice(card, variant);
                if (this.selected.has(productId)) {
                  this.selected.set(productId, {
                    variantId: variant.id,
                    price: variant.price
                  });
                }
                this.update();
              }.bind(this)
            );
          }
        }.bind(this)
      );
    }

    initAddButton() {
      if (!this.addButton) return;
      this.addButton.addEventListener(
        'click',
        function () {
          this.addToCart();
        }.bind(this)
      );
    }

    toggleCard(card, productId) {
      var toggle = card.querySelector('[data-bb-toggle]');
      if (this.selected.has(productId)) {
        this.selected.delete(productId);
        card.classList.remove('bb-card--selected');
        if (toggle) toggle.setAttribute('aria-pressed', 'false');
      } else {
        this.selected.set(productId, {
          variantId: card._bbVariant.id,
          price: card._bbVariant.price
        });
        card.classList.add('bb-card--selected');
        if (toggle) toggle.setAttribute('aria-pressed', 'true');
      }
      this.update();
    }

    renderCardPrice(card, variant) {
      var priceEl = card.querySelector('[data-bb-price]');
      if (priceEl) {
        priceEl.innerHTML = this.formatMoney(variant.price);
      }
      var compareEl = card.querySelector('[data-bb-compare]');
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.innerHTML = this.formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
    }

    /**
     * Returns the highest tier unlocked for a given item count (or null),
     * and the next tier the customer is working towards.
     */
    getTierState(count) {
      var unlocked = null;
      var next = null;
      this.config.tiers.forEach(function (tier) {
        if (count >= tier.qty) {
          unlocked = tier;
        } else if (!next) {
          next = tier;
        }
      });
      return { unlocked: unlocked, next: next };
    }

    update() {
      var count = this.selected.size;
      var state = this.getTierState(count);

      this.renderMessage(count, state);
      this.renderProgress(count, state);
      this.renderTotals(state);
      this.renderButton(count);
    }

    renderMessage(count, state) {
      if (!this.messageEl) return;
      var messages = this.config.messages;
      var text;

      if (state.next) {
        var remaining = state.next.qty - count;
        var template = remaining === 1 ? messages.single : messages.plural;
        text = template
          .split('[count]')
          .join(remaining)
          .split('[discount]')
          .join(state.next.off);
      } else if (state.unlocked) {
        text = messages.unlocked.split('[discount]').join(state.unlocked.off);
      } else {
        text = '';
      }

      this.messageEl.textContent = text;
      this.messageEl.classList.toggle('bb-message--unlocked', !state.next && !!state.unlocked);
    }

    renderProgress(count, state) {
      this.segments.forEach(function (segment, index) {
        segment.classList.toggle('bb-progress__segment--filled', index < count);
      });

      this.badges.forEach(function (badge) {
        var qty = parseInt(badge.getAttribute('data-bb-badge'), 10);
        badge.classList.toggle('bb-progress__badge--active', count >= qty);
      });
    }

    renderTotals(state) {
      var subtotal = 0;
      this.selected.forEach(function (item) {
        subtotal += item.price;
      });

      var discountPercent = state.unlocked ? state.unlocked.off : 0;
      var finalTotal = Math.round(subtotal * (100 - discountPercent) / 100);
      var savings = subtotal - finalTotal;

      if (this.totalOriginalEl) {
        this.totalOriginalEl.textContent = this.formatMoney(subtotal);
        this.totalOriginalEl.classList.toggle('bb-total__original--struck', savings > 0);
      }
      if (this.totalFinalEl) {
        this.totalFinalEl.textContent = this.formatMoney(finalTotal);
      }
      if (this.savingsEl) {
        if (savings > 0) {
          this.savingsEl.hidden = false;
          this.savingsEl.textContent = this.config.messages.savings
            .split('[amount]')
            .join(this.formatMoney(savings));
        } else {
          this.savingsEl.hidden = true;
          this.savingsEl.textContent = '';
        }
      }
    }

    renderButton(count) {
      if (!this.addButton) return;
      var ready = count >= this.config.minQty && !this.isAdding;
      this.addButton.disabled = !ready;
      this.addButton.classList.toggle('bb-add--ready', ready);

      if (this.addButtonLabel && !this.isAdding) {
        var label = this.config.messages.button;
        if (this.config.messages.buttonCount) {
          label = label + ' (' + count + ')';
        }
        this.addButtonLabel.textContent = label;
      }
    }

    /**
     * Applies the discount code of the currently unlocked tier through
     * Shopify's /discount/{code} endpoint (sets the discount cookie on
     * the session so the code is applied in cart + checkout).
     */
    applyDiscountCode(count) {
      var state = this.getTierState(count);
      var code = state.unlocked && state.unlocked.code ? state.unlocked.code : '';
      if (!code) return Promise.resolve();

      var url = this.config.discountPath.replace('[code]', encodeURIComponent(code));
      return fetch(url, { credentials: 'same-origin' }).catch(function () {
        // Non-blocking: items are still added without the code
      });
    }

    addToCart() {
      if (this.isAdding || this.selected.size < this.config.minQty) return;
      this.isAdding = true;
      this.addButton.disabled = true;
      this.addButton.classList.add('bb-add--loading');
      if (this.addButtonLabel) {
        this.addButtonLabel.textContent = this.config.messages.adding;
      }

      var items = [];
      this.selected.forEach(function (item) {
        items.push({ id: item.variantId, quantity: 1 });
      });

      this.applyDiscountCode(items.length)
        .then(
          function () {
            return fetch(this.config.routes.cartAdd, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ items: items })
            });
          }.bind(this)
        )
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (error) {
              throw error;
            });
          }
          return response.json();
        })
        .then(
          function () {
            this.isAdding = false;
            this.addButton.classList.remove('bb-add--loading');
            this.afterAdd();
            this.update();
          }.bind(this)
        )
        .catch(
          function (error) {
            console.error('BundleBuilder: could not add bundle to cart', error);
            this.isAdding = false;
            this.addButton.classList.remove('bb-add--loading');
            if (this.addButtonLabel) {
              this.addButtonLabel.textContent = this.config.messages.error;
              setTimeout(
                function () {
                  this.update();
                }.bind(this),
                2000
              );
            } else {
              this.update();
            }
          }.bind(this)
        );
    }

    afterAdd() {
      var goToCart = this.config.redirectToCart || this.config.cartType !== 'drawer';

      if (goToCart) {
        window.location.href = this.config.routes.cartPage;
        return;
      }

      // Let the theme rebuild + open the cart drawer (theme.js CartDrawer)
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          bubbles: true,
          detail: { source: 'bundle-builder' }
        })
      );
    }

    formatMoney(cents) {
      if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        return theme.Currency.formatMoney(cents, this.config.moneyFormat);
      }
      return this.fallbackMoney(cents);
    }

    /**
     * Minimal money formatter used only when theme.Currency is unavailable
     * (e.g. in the theme editor preview iframe edge cases).
     */
    fallbackMoney(cents) {
      var format = this.config.moneyFormat || '${{amount}}';
      var value = (cents / 100).toFixed(2);

      function placeholderRegex(name) {
        return new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}');
      }

      var withDelimiters = function (number, precision, thousands, decimal) {
        precision = precision == null ? 2 : precision;
        thousands = thousands || ',';
        decimal = decimal || '.';
        var parts = Number(number).toFixed(precision).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.length > 1 ? parts.join(decimal) : parts[0];
      };

      if (placeholderRegex('amount_no_decimals_with_comma_separator').test(format)) {
        value = withDelimiters(cents / 100, 0, '.', ',');
      } else if (placeholderRegex('amount_with_comma_separator').test(format)) {
        value = withDelimiters(cents / 100, 2, '.', ',');
      } else if (placeholderRegex('amount_no_decimals').test(format)) {
        value = withDelimiters(cents / 100, 0);
      } else if (placeholderRegex('amount_with_space_separator').test(format)) {
        value = withDelimiters(cents / 100, 2, ' ', '.');
      } else if (placeholderRegex('amount_with_apostrophe_separator').test(format)) {
        value = withDelimiters(cents / 100, 2, "'", '.');
      } else {
        value = withDelimiters(cents / 100, 2);
      }

      return format.replace(/\{\{\s*\w+\s*\}\}/, value);
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
